// src/components/CancellationFeedbackModal.jsx
import React, { useState } from "react";
import { submitFeedback } from "../utils/api";

const FEEDBACK_REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using", label: "Not using it enough" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "found_alternative", label: "Found a better alternative" },
  { value: "technical_issues", label: "Technical issues" },
  { value: "other", label: "Other" },
];

export default function CancellationFeedbackModal({ onClose, onConfirmCancel }) {
  const [selectedReason, setSelectedReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (skipFeedback = false) => {
    setIsSubmitting(true);
    
    try {
      // Only submit feedback if user provided any
      if (!skipFeedback && (feedback.trim() || selectedReason)) {
        await submitFeedback(feedback.trim(), selectedReason || null);
      }
      
      // Proceed with cancellation
      await onConfirmCancel();
    } catch (error) {
      console.error("Error during cancellation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-5">
          <h2 className="text-xl font-semibold text-white">Before you go...</h2>
          <p className="text-slate-300 text-sm mt-1">We'd love to hear your feedback (optional)</p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Reason Selection */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What's the main reason you're canceling?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_REASONS.map((reason) => (
                <button
                  key={reason.value}
                  type="button"
                  onClick={() => setSelectedReason(reason.value)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-all text-left ${
                    selectedReason === reason.value
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 hover:border-gray-300 text-gray-600"
                  }`}
                >
                  {reason.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Feedback Text */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Any additional feedback? (optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us how we can improve..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-700 placeholder-gray-400"
            />
          </div>
          
          {/* Info Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
            <p className="text-sm text-amber-800">
              <span className="font-medium">Note:</span> After cancellation, you'll keep access to premium features until the end of your current billing period.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Keep Subscription
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
            >
              Skip & Cancel
            </button>
            <button
              onClick={() => handleSubmit(false)}
              disabled={isSubmitting}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Processing...
                </>
              ) : (
                "Submit & Cancel"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


